import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Search } from "lucide-react";
import { PageTitle } from "@/components/SettingsPanel";
import { VenueReviewList } from "@/components/VenueReviewList";
import { TypeBadge, StatusPill } from "@/components/VenueBits";
import { imgSrc } from "@/lib/img";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { User, Venue } from "@/types";
import { useLive } from "@/lib/live";
import { toast } from "sonner";
import { Switch } from "@/components/ui-kit";
import { errorText } from "@/lib/api";

const PlaceCard = ({ v, canEdit, onChanged }: { v: Venue & { account?: any }; canEdit: boolean; onChanged: () => void }) => {
  const { t, lang } = usePrefs();
  const toggle = async (enabled: boolean) => {
    try { await api.patch(`/admin/venues/${v.venue_id}/events`, { enabled }); toast.success(t(enabled ? "pp.eventsOnToast" : "pp.eventsOffToast")); onChanged(); } catch (e) { toast.error(errorText(e, t)); }
  };
  const live = (v.review_status || "approved") === "approved" && v.published && !v.account?.disabled;
  return (
    <div data-testid={`admin-place-${v.venue_id}`} className={`rounded-2xl border bg-card p-2.5 flex items-center gap-3 min-w-0 ${live ? "" : "border-dashed"}`}>
      <img src={imgSrc(v.image)} alt="" className={`h-14 w-14 shrink-0 rounded-xl object-cover ${live ? "" : "grayscale opacity-60"}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="font-bold truncate">{lang === "ar" ? v.name_ar : v.name_en || v.name_ar}</p>
          {v.review_status && v.review_status !== "approved" && <StatusPill status={v.review_status} />}
          {v.account?.disabled && <span className="shrink-0 rounded-full bg-zinc-700 text-white px-2 py-0.5 text-[10px] font-bold">{t("ap.partnerDisabled")}</span>}
        </div>
        <p className="text-xs text-muted-foreground truncate">{[v.account?.business_name, v.city].filter(Boolean).join(" · ")}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-2"><TypeBadge type={v.type} />
          {(v.review_status || "approved") === "approved" && (
            <label className="inline-flex items-center gap-1.5 text-[11px] font-bold text-muted-foreground">
              {t("pp.eventsShort")}<Switch data-testid={`admin-place-events-${v.venue_id}`} checked={!!v.events_enabled} disabled={!canEdit} onCheckedChange={toggle} />
            </label>
          )}
        </div>
      </div>
      {live && <Link to={`/venues/${v.venue_id}`} target="_blank" aria-label={t("ap.view")} data-testid={`admin-place-view-${v.venue_id}`} className="h-9 w-9 shrink-0 grid place-items-center rounded-full border text-primary hover:bg-accent"><ExternalLink className="h-4 w-4" /></Link>}
    </div>
  );
};

export default function AdminPlaces() {
  const { t } = usePrefs();
  const me = useAuth().user as User;
  const isSuper = (me.admin_role || "super") === "super";
  const [list, setList] = useState<Venue[] | null>(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/places").then((r) => setList(r.data)).catch(() => setList([]));
  useLive(() => load());
  useEffect(() => { load(); }, []);
  const n = q.trim().toLowerCase();
  const shown = list?.filter((v: any) => !n || [v.name_ar, v.name_en, v.city, v.account?.business_name].some((x) => x && String(x).toLowerCase().includes(n)));
  return (
    <div data-testid="admin-places-page">
      <PageTitle title={t("ap.title")} sub={t("ap.sub")} />
      <VenueReviewList canDecide={isSuper} onDecided={load} />
      <h2 className="font-display text-xl font-black mb-3">{t("ap.all")}{list && <span className="ms-2 inline-flex align-middle rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-sm font-bold" dir="ltr">{list.length}</span>}</h2>
      {!!list?.length && (
        <div className="relative mb-3">
          <Search className="h-4 w-4 absolute top-1/2 -translate-y-1/2 start-3 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("ap.search")} data-testid="admin-places-search" className="h-10 w-full rounded-full border bg-card ps-9 pe-4 text-sm" />
        </div>
      )}
      {list?.length === 0 && <p data-testid="admin-places-empty" className="text-muted-foreground py-10 text-center">{t("ap.empty")}</p>}
      <div className="grid gap-2 lg:grid-cols-2">{shown?.map((v) => <PlaceCard key={v.venue_id} v={v} canEdit={isSuper} onChanged={load} />)}</div>
    </div>
  );
}
